import { Modal } from "../../../components/ui/Modal";
import { formatCurrency, formatDate } from "../../../lib/utils";
import CrewDropdownMenu from "../../driver/ui/CrewDropDownMenu";
import DriverDropdownMenu from "../../driver/ui/DriverDropdownMenu";
import { Dispatch } from "../@types";

interface DispatchHistoryModalProps {
  dispatch: Dispatch;
  isOpen: boolean;
  onClose: () => void;
}

const DispatchHistoryModal: React.FC<DispatchHistoryModalProps> = ({
  dispatch,
  isOpen,
  onClose,
}) => {
  const fuel = dispatch.fuelExpense;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Dispatch History"
      className="max-w-4xl p-2"
    >
      <div className="p-10">
        <div className="border border-gray-300 rounded-[10px] p-4 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* ================= Trip Overview ================= */}
          <section className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-2">
              Trip Overview
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <Info label="Incidents" value={dispatch?.dispatchNumber} />
              <Info label="Status" value={dispatch.tripStatus} />
              <Info
                label="Completed"
                value={dispatch.isCompleted ? "Yes" : "No"}
              />
              <Info
                label="Start Time"
                value={formatDate(dispatch.tripStartTime)}
              />
              <Info label="End Time" value={formatDate(dispatch.tripEndTime)} />
              <Info
                label="Estimated Time"
                value={`${dispatch.estimatedTime} hrs`}
              />
              <Info
                label="Distance"
                value={`${dispatch.approximateDistance} km`}
              />
            </div>
          </section>

          {/* ================= Locations ================= */}
          <section className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-2">Route</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Info label="From" value={dispatch.fromLocation} />
              <Info label="To" value={dispatch.toLocation} />
            </div>
          </section>

          {/* ================= Vehicle & Company ================= */}
          <section className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-2">
              Vehicle & Company
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Info label="Vehicle Model" value={dispatch.vehicleModel} />
              <Info
                label="Incidents"
                value={dispatch?.noOfIncidents?.toString()}
              />
            </div>
          </section>

          {/* ================= Driver & Crew ================= */}
          <section className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-2">
              Driver & Crew
            </h3>

            <DriverDropdownMenu
              value={dispatch.driverId}
              onChange={() => {}}
              disabled
              className="bg-[#F6F6F6] h-[48px] w-full"
            />

            {dispatch.crews?.length > 0 && (
              <div className="space-y-2">
                {dispatch.crews.map((crew) => (
                  <div key={crew.id} className="bg-gray-50 p-3 rounded text-sm">
                    <span className="font-medium">{crew.crewType}</span>
                    <CrewDropdownMenu
                      value={crew.id}
                      onChange={() => {}}
                      disabled
                      showLabel={false}
                      className="bg-[#F6F6F6] h-[48px] w-full mt-2"
                    />
                  </div>
                ))}
              </div>
            )}

            {dispatch.crewMembers && (
              <Info label="Crew Members (raw)" value={dispatch.crewMembers} />
            )}
          </section>

          {/* ================= Expenses ================= */}
          <section className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-2">Expenses</h3>

            {fuel && (
              <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
                <p className="text-hColor font-semibold">Fuel Expense</p>
                <p>Type: {fuel.fuelType}</p>
                <p>Station: {fuel.stationName}</p>
                <p>Date: {formatDate(fuel.fuelDate)}</p>
                <p>Quantity: {fuel.quantity} L</p>
                <p>Price / Unit: {formatCurrency(fuel.pricePerUnit)}</p>
                <p>Odometer: {fuel.odometerReading}</p>
                <p>Total: {formatCurrency(fuel.amount)}</p>
              </div>
            )}

            {dispatch.expenses?.map((exp) => (
              <div key={exp.id} className="bg-gray-50 p-3 rounded text-sm">
                {exp.description} – {formatCurrency(exp.amount)}
              </div>
            ))}
          </section>

          {/* ================= Income ================= */}
          <section className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-2">Income</h3>

            {dispatch.incomes?.map((inc) => (
              <div key={inc.id} className="bg-gray-50 p-3 rounded text-sm">
                <p className="font-medium">{inc.description}</p>
                <p>
                  Expected: {formatCurrency(inc.expectedAmount)} | Earned:{" "}
                  {formatCurrency(inc.earnedAmount)}
                </p>
              </div>
            ))}
          </section>

          {/* ================= Summary ================= */}
          <section className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-2">
              Financial Summary
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <Info
                label="Total Income"
                value={formatCurrency(dispatch.totalIncome)}
              />
              <Info
                label="Total Expense"
                value={formatCurrency(dispatch.totalExpense)}
                color="text-red-600"
              />
              <Info
                label="Net Banked"
                value={formatCurrency(dispatch.netBanked)}
                color="text-green-600"
              />
            </div>
          </section>
        </div>
      </div>
    </Modal>
  );
};

export default DispatchHistoryModal;

/* ---------- Small helper ---------- */
const Info = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <div>
    <p className={`text-xs mb-1 text-gray-500`}>{label}</p>
    <div
      className={`bg-[#F6F6F6] h-[40px] px-3 flex items-center rounded-md text-sm ${
        color ? color : ""
      }`}
    >
      {value}
    </div>
  </div>
);
